import { formatDate, formatRelativeDay, formatTime, formatWeekday, israelDay, SOURCE_LABELS, TASK_KIND_LABELS } from '@takumi/shared';
import { AlertTriangle, CalendarClock, CalendarDays, Check, CircleAlert, Clock, Inbox, ListTodo, Sparkles, Sunrise, Users } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { PageHeader } from '@/components/AppShell';
import { Button } from '@/components/ui/button';
import { Badge, Card, CardHeader, EmptyState, PageLoader } from '@/components/ui/primitives';
import { useAuth } from '@/lib/auth';
import { useMyDay } from '@/lib/queries';
import type { NewLeadRow, Task } from '@/lib/types';
import { cn } from '@/lib/utils';
import { StageBadge } from '@/features/crm/badges';
import { ContactActions } from '@/features/crm/ContactActions';
import { CompleteTaskDialog } from '@/features/crm/TaskDialogs';
import { LogActivityDialog } from '@/features/crm/LogActivityDialog';
import { MyDaySales } from '@/features/sales/MyDaySales';

function greeting() {
  const h = Number(new Intl.DateTimeFormat('en-GB', { timeZone: 'Asia/Jerusalem', hour: 'numeric', hour12: false }).format(new Date()));
  return h < 12 ? 'בוקר טוב' : h < 17 ? 'צהריים טובים' : 'ערב טוב';
}

export function MyDayPage() {
  const { me, can } = useAuth();
  const [scope, setScope] = useState<'me' | 'all'>('me');
  const { data, isLoading } = useMyDay(scope);

  if (isLoading || !data) return <PageLoader />;

  const firstName = me?.user.fullName.split(' ')[0];
  const urgent = data.overdue.length + data.today.length + data.newLeads.length;

  return (
    <div className="mx-auto max-w-6xl">
      <PageHeader
        title={`${greeting()}, ${firstName}`}
        subtitle={`${formatWeekday(new Date())}, ${formatDate(israelDay())} · ${urgent ? `${urgent} פריטים דורשים טיפול היום` : 'אין פריטים דחופים — זמן מצוין ליצור קשר עם לקוחות'}`}
        actions={
          can('reports.read') && (
            <div className="flex rounded-control border border-line-strong bg-surface p-0.5 text-sm">
              {(['me', 'all'] as const).map((s) => (
                <button key={s} onClick={() => setScope(s)} className={cn('rounded-md px-3 py-1 font-semibold', scope === s ? 'bg-matcha-700 text-white' : 'text-ink-soft')}>
                  {s === 'me' ? 'שלי' : 'כל הצוות'}
                </button>
              ))}
            </div>
          )
        }
      />

      <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat label="פולואפים באיחור" value={data.overdue.length} tone="overdue" icon={<CircleAlert />} />
        <Stat label="פולואפים להיום" value={data.today.length} tone="today" icon={<Clock />} />
        <Stat label="לידים חדשים" value={data.newLeads.length} tone="gold" icon={<Sparkles />} />
        <Stat label="פגישות היום" value={data.meetings.length} tone="matcha" icon={<Users />} />
      </div>

      {data.pendingReviews > 0 && (
        <Link to="/inbox" className="mb-5 flex items-center gap-3 rounded-card border border-today/30 bg-today-bg px-4 py-3 text-sm font-semibold text-today hover:brightness-[0.98]">
          <Inbox className="size-5" />
          {data.pendingReviews === 1 ? 'פנייה אחת ממתינה להחלטה (חשד לכפילות)' : `${data.pendingReviews} פניות ממתינות להחלטה (חשד לכפילות)`}
          <span className="ms-auto underline">לטיפול</span>
        </Link>
      )}

      <div className="grid gap-5 lg:grid-cols-[1.35fr_1fr]">
        <div className="flex flex-col gap-5">
          <Section title="פולואפים באיחור" icon={<CircleAlert />} count={data.overdue.length} tone="overdue">
            {data.overdue.length ? data.overdue.map((t) => <TaskRow key={t.id} task={t} showDate />) : <Clean text="אין פולואפים באיחור. יפה!" />}
          </Section>
          <Section title="פולואפים להיום" icon={<Clock />} count={data.today.length} tone="today">
            {data.today.length ? data.today.map((t) => <TaskRow key={t.id} task={t} />) : <Clean text="אין פולואפים נוספים להיום" />}
          </Section>
          <Section title="לידים חדשים — ממתינים ליצירת קשר ראשונה" icon={<Sparkles />} count={data.newLeads.length}>
            {data.newLeads.length ? data.newLeads.map((l) => <NewLeadRowView key={l.id} lead={l} />) : <Clean text="כל הלידים החדשים טופלו" />}
          </Section>
        </div>

        <div className="flex flex-col gap-5">
          <Section title="פגישות היום" icon={<CalendarDays />} count={data.meetings.length}>
            {data.meetings.length ? data.meetings.map((t) => <TaskRow key={t.id} task={t} />) : <Clean text="אין פגישות היום" />}
          </Section>
          {data.tasks.length > 0 && (
            <Section title="משימות" icon={<ListTodo />} count={data.tasks.length}>
              {data.tasks.map((t) => <TaskRow key={t.id} task={t} showDate />)}
            </Section>
          )}
          <Section title="אין פולואפ עתידי" icon={<AlertTriangle />} count={data.noFollowup.length} tone={data.noFollowup.length ? 'overdue' : undefined}>
            {data.noFollowup.length ? (
              data.noFollowup.map((o) => (
                <Link key={o.id} to={`/org/${o.id}`} className="flex items-center gap-3 px-4 py-2.5 hover:bg-sunken/60">
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-semibold">{o.name}</div>
                    <div className="text-xs text-muted">
                      {o.lastContactAt ? `קשר אחרון ${formatRelativeDay(o.lastContactAt, new Date(), false)}` : 'לא נוצר קשר'}
                    </div>
                  </div>
                  <StageBadge name={o.stageName} color={o.stageColor} />
                </Link>
              ))
            ) : (
              <Clean text="לכל ליד פתוח יש פולואפ מתוכנן" />
            )}
          </Section>
          <Section title="מחר" icon={<Sunrise />} count={data.tomorrow.length}>
            {data.tomorrow.length ? (
              data.tomorrow.map((t) => (
                <Link key={t.id} to={t.organizationId ? `/org/${t.organizationId}` : '/tasks'} className="flex items-center gap-3 px-4 py-2 text-sm hover:bg-sunken/60">
                  <span className="w-11 text-xs text-muted tabular">{formatTime(t.dueAt)}</span>
                  <span className="min-w-0 flex-1 truncate">
                    <strong>{t.organizationName ?? t.title}</strong> · {t.title}
                  </span>
                </Link>
              ))
            ) : (
              <Clean text="מחר עדיין פנוי" />
            )}
          </Section>
        </div>
      </div>
      <MyDaySales scope={scope} />
    </div>
  );
}

function Stat({ label, value, tone, icon }: { label: string; value: number; tone: 'overdue' | 'today' | 'gold' | 'matcha'; icon: ReactNode }) {
  const tones = {
    overdue: value ? 'text-overdue bg-overdue-bg' : 'text-muted bg-sunken',
    today: value ? 'text-today bg-today-bg' : 'text-muted bg-sunken',
    gold: value ? 'text-gold-700 bg-gold-100' : 'text-muted bg-sunken',
    matcha: value ? 'text-matcha-800 bg-matcha-100' : 'text-muted bg-sunken',
  };
  return (
    <Card className="flex items-center gap-3 p-4">
      <span className={cn('grid size-10 place-items-center rounded-full [&_svg]:size-5', tones[tone])}>{icon}</span>
      <div>
        <div className="font-display text-2xl leading-none font-semibold tabular">{value}</div>
        <div className="mt-1 text-xs font-semibold text-muted">{label}</div>
      </div>
    </Card>
  );
}

function Section({ title, icon, count, tone, children }: { title: string; icon: ReactNode; count: number; tone?: 'overdue' | 'today'; children: ReactNode }) {
  return (
    <Card className={cn('overflow-hidden', tone === 'overdue' && count > 0 && 'border-overdue/30', tone === 'today' && count > 0 && 'border-today/30')}>
      <CardHeader title={title} icon={icon} count={count} />
      <div className="divide-y divide-line/70">{children}</div>
    </Card>
  );
}

function Clean({ text }: { text: string }) {
  return (
    <div className="flex items-center gap-2 px-4 py-4 text-sm text-muted">
      <Check className="size-4 text-success" /> {text}
    </div>
  );
}

function TaskRow({ task, showDate }: { task: Task; showDate?: boolean }) {
  const [completing, setCompleting] = useState(false);
  const [logging, setLogging] = useState<null | 'call' | 'whatsapp' | 'email'>(null);
  const contacts = task.contactName ? [{ id: task.contactId ?? '', fullName: task.contactName, isPrimary: true }].filter((c) => c.id) : [];
  return (
    <div className="group flex items-center gap-3 px-4 py-3">
      <div className="w-14 shrink-0 text-center">
        <div className="text-sm font-semibold tabular">{formatTime(task.dueAt)}</div>
        {showDate && <div className="text-[11px] text-overdue">{formatRelativeDay(task.dueAt, new Date(), false)}</div>}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          {task.organizationId ? (
            <Link to={`/org/${task.organizationId}`} className="truncate font-semibold hover:text-matcha-700 hover:underline">
              {task.organizationName}
            </Link>
          ) : (
            <span className="truncate font-semibold">{task.title}</span>
          )}
          {task.kind !== 'followup' && <Badge variant="outline">{TASK_KIND_LABELS[task.kind]}</Badge>}
        </div>
        <div className="truncate text-xs text-muted">
          {[task.organizationId ? task.title : null, task.contactName, task.location, task.stageName].filter(Boolean).join(' · ')}
        </div>
      </div>
      <ContactActions phone={task.contactPhone} whatsapp={task.contactWhatsapp} email={task.contactEmail} onAction={(t) => task.organizationId && setLogging(t)} />
      <Button size="sm" variant="secondary" onClick={() => setCompleting(true)} aria-label="סימון כבוצע">
        <Check /> בוצע
      </Button>
      <CompleteTaskDialog task={task} open={completing} onOpenChange={setCompleting} />
      {task.organizationId && logging && (
        <LogActivityDialog
          open={!!logging}
          onOpenChange={(o) => !o && setLogging(null)}
          orgId={task.organizationId}
          orgName={task.organizationName ?? ''}
          contacts={contacts}
          openFollowup={task}
          defaultType={logging}
        />
      )}
    </div>
  );
}

function NewLeadRowView({ lead }: { lead: NewLeadRow }) {
  const [logging, setLogging] = useState<null | 'call' | 'whatsapp' | 'email'>(null);
  return (
    <div className="flex items-center gap-3 px-4 py-3">
      <div className="min-w-0 flex-1">
        <Link to={`/org/${lead.id}`} className="font-semibold hover:text-matcha-700 hover:underline">
          {lead.name}
        </Link>
        <div className="truncate text-xs text-muted">
          {[lead.contactName, lead.businessType, lead.city, SOURCE_LABELS[lead.source], `נכנס ${formatRelativeDay(lead.createdAt)}`].filter(Boolean).join(' · ')}
        </div>
      </div>
      {!lead.ownerId && <Badge variant="gold">לא משויך</Badge>}
      <ContactActions phone={lead.contactPhone} whatsapp={lead.contactWhatsapp} email={lead.contactEmail} onAction={setLogging} />
      <Button size="sm" asChild variant="secondary">
        <Link to={`/org/${lead.id}`}>
          <CalendarClock /> פתיחה
        </Link>
      </Button>
      {logging && (
        <LogActivityDialog
          open
          onOpenChange={(o) => !o && setLogging(null)}
          orgId={lead.id}
          orgName={lead.name}
          contacts={lead.contactId && lead.contactName ? [{ id: lead.contactId, fullName: lead.contactName, isPrimary: true }] : []}
          openFollowup={lead.followupId ? { id: lead.followupId, title: lead.followupTitle ?? '', dueAt: lead.followupDueAt ?? '' } : null}
          defaultType={logging}
        />
      )}
    </div>
  );
}
